import { useState, useEffect, useCallback, useRef } from 'react';
import { ViewType } from '../types';

interface SwipeNavigationOptions {
  onViewChange: (view: ViewType) => void;
  currentView: ViewType;
  views: ViewType[];
}

export const useSwipeNavigation = ({ onViewChange, currentView, views }: SwipeNavigationOptions) => {
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const [isSwiping, setIsSwiping] = useState(false);
  const [swipeDirection, setSwipeDirection] = useState<'left' | 'right' | null>(null);
  const touchStartTime = useRef<number | null>(null);

  // Minimum distance for a swipe
  const minSwipeDistance = 30; // Reduced for better mobile responsiveness
  const maxSwipeTime = 300; // Reduced for more responsive swipes

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
    touchStartTime.current = Date.now();
    setIsSwiping(true);
    setSwipeDirection(null);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    if (!touchStart) return;
    
    const currentX = e.targetTouches[0].clientX;
    setTouchEnd(currentX);
    
    const distance = touchStart - currentX;
    if (Math.abs(distance) > 5) { // Reduced threshold for more sensitive detection
      setSwipeDirection(distance > 0 ? 'left' : 'right');
    }
  };

  const onTouchEnd = useCallback(() => {
    if (!touchStart || !touchEnd || !touchStartTime.current) {
      setIsSwiping(false);
      setSwipeDirection(null);
      return;
    }
    
    const distance = touchStart - touchEnd;
    const swipeTime = Date.now() - touchStartTime.current;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    const isValidSwipe = (isLeftSwipe || isRightSwipe) && swipeTime < maxSwipeTime;

    if (isValidSwipe) {
      const currentIndex = views.indexOf(currentView);
      let nextIndex: number;

      if (isLeftSwipe) {
        // Swipe left - go to next view
        nextIndex = currentIndex < views.length - 1 ? currentIndex + 1 : 0;
      } else {
        // Swipe right - go to previous view
        nextIndex = currentIndex > 0 ? currentIndex - 1 : views.length - 1;
      }

      // Add haptic feedback if available
      if ('vibrate' in navigator) {
        navigator.vibrate(50);
      }

      onViewChange(views[nextIndex]);
    }
    
    setIsSwiping(false);
    setSwipeDirection(null);
  }, [touchStart, touchEnd, currentView, views, onViewChange, minSwipeDistance, maxSwipeTime]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        e.preventDefault();
        const currentIndex = views.indexOf(currentView);
        let nextIndex: number;

        if (e.key === 'ArrowLeft') {
          nextIndex = currentIndex > 0 ? currentIndex - 1 : views.length - 1;
        } else {
          nextIndex = currentIndex < views.length - 1 ? currentIndex + 1 : 0;
        }

        onViewChange(views[nextIndex]);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentView, views, onViewChange]);

  return {
    onTouchStart,
    onTouchMove,
    onTouchEnd,
    isSwiping,
    swipeDirection,
  };
};
